import React, { ReactElement } from 'react';
import { useReactToPrint } from 'react-to-print';
import ImageButton from 'components/common/Buttons/ImageButton';
import { Box, Paper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import CloseButton from '../Buttons/CloseButton';

type Props = {
  afterPrint: () => void;
  onClose: () => void;
  text: string;
  refToPrint: any;
  children: JSX.Element;
};

const PrintModal = ({ afterPrint, onClose, text, refToPrint, children }: Props): ReactElement => {
  const classes = useStyles();

  const printFn = useReactToPrint({
    contentRef: refToPrint,
    pageStyle:
      '@media print { html, body { height: auto; margin: 0 !important; padding: 0 !important;}}',
    onAfterPrint: afterPrint,
  });

  const handlePrint = () => {
    printFn();
  };

  return (
    <Box className={classes.container}>
      <Paper className={classes.background}>
        <CloseButton onClick={onClose} className={classes.closeButton} color="inherit" />
        <Box className={classes.contentWrapper}>{children}</Box>
        <Box className={classes.footer}>
          <Typography variant="h3" className={classes.text}>
            {text}
          </Typography>
          <ImageButton onClick={handlePrint} imageName={'printButton'} />
        </Box>
      </Paper>
    </Box>
  );
};

export default PrintModal;

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100vh',
    width: '100vw',
    display: 'flex',
  },
  background: {
    margin: 'auto',
    width: '80%',
    height: '80%',
    position: 'relative',
    borderRadius: remCalc(25),

    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'space-between',
    flexFlow: 'column',

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  text: {
    textAlign: 'center',
    flexGrow: 1,
    [theme.breakpoints.down('xs')]: {
      fontSize: '4vmin',
      lineHeight: 1,
    },
    [theme.breakpoints.up('md')]: {
      flexGrow: 0,
    },
  },
  footer: {
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: remCalc(48),
    maxHeight: '20%',
    [theme.breakpoints.down('xs')]: {
      padding: remCalc(8),
    },
    [theme.breakpoints.up('md')]: {
      justifyContent: 'space-evenly',
    },
  },
  contentWrapper: {
    boxSizing: 'border-box',
    width: '100%',
    paddingTop: remCalc(24),
    paddingRight: remCalc(45),
    paddingLeft: remCalc(45),
    flexGrow: 1,
    maxHeight: '75%',
    '&> h*': {
      textAlign: 'center',
    },
  },
  closeButton: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
}));
